import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { useId } from 'react-id-generator';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { useResponsiveChildren } from '../../hooks/use_responsive_children.js';
import { useTranslations } from '../../hooks/use_translations.js';
import { spacing8x } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { ButtonMenu } from '../ButtonMenu/ButtonMenu.js';
import { IconMore } from '../Icons/IconMore.js';
import { ActionToolbarDivider } from './ActionToolbarDivider.js';
import { ActionToolbarItemWrapper } from './ActionToolbarItem.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["aria-label", "children"];
var ActionToolbarGroupMoreButton = styled.div.withConfig({
  displayName: "ActionToolbarGroup__ActionToolbarGroupMoreButton",
  componentId: "sc-zzrcvb-0"
})(["&&{display:inline-flex;align-items:center;width:0;}&& button{min-height:15px;padding:5px 0;margin-left:-10px;color:", ";}&& button:hover,&& button:focus{color:", ";background:", ";}"], function (props) {
  return props.theme.neutralColors.white;
}, function (props) {
  return props.theme.neutralColors.white;
}, function (props) {
  return props.theme.neutralColors.smoke;
});
var ActionToolbarGroupWrapper = styled.div.withConfig({
  displayName: "ActionToolbarGroup__ActionToolbarGroupWrapper",
  componentId: "sc-zzrcvb-1"
})(["&&{height:48px;margin:0 auto;padding:11px ", ";border-radius:", ";background:", ";color:", ";}&& ", ":last-of-type > *{margin-right:0;}"], spacing8x, spacing8x, function (props) {
  return props.theme.neutralColors.charcoal;
}, function (props) {
  return props.theme.neutralColors.white;
}, ActionToolbarItemWrapper);
var isDivider = function isDivider(child) {
  return typeof child !== "string" && typeof child !== "number" && (child === null || child === void 0 ? void 0 : child.type) === ActionToolbarDivider;
};
var findPriorities = function findPriorities(c) {
  var priorities = [];
  c.forEach(function (item, i) {
    var isPriorityItem = item.props.priority || !!item.props.as;
    if (isPriorityItem) priorities.push(i);
  });
  var priorityGroups = c.filter(function (item, i) {
    return priorities.includes(i);
  }).map(function (item) {
    return item.props.group;
  });
  c.forEach(function (item, i) {
    var isPriorityItem = isDivider(item) && priorityGroups.includes(item.props.group);
    if (isPriorityItem) priorities.push(i);
  });
  return priorities.sort(function (a, b) {
    return a - b;
  });
};
var ActionToolbarGroup = /*#__PURE__*/React.forwardRef(function ActionToolbarGroup2(props, ref) {
  var _useId = useId(1, "toolbarButton-"),
    _useId2 = _slicedToArray(_useId, 1),
    defaultLabel = _useId2[0];
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  var _props$ariaLabel = props["aria-label"],
    ariaLabel = _props$ariaLabel === void 0 ? defaultLabel : _props$ariaLabel,
    children = props.children,
    rest = _objectWithoutProperties(props, _excluded);
  var _React$useState = React.useState(0),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    focusedIndex = _React$useState2[0],
    setFocusedIndex = _React$useState2[1];
  var toolbarRef = React.useRef(null);
  var overflowMenuRef = React.useRef(null);
  var preservedIndex = React.useMemo(function () {
    return findPriorities(children);
  }, [children]);
  var _useResponsiveChildre = useResponsiveChildren({
      children: children,
      parentRef: toolbarRef,
      preservedIndex: preservedIndex,
      getRefProp: function getRefProp(child) {
        return !isDivider(child) ? "wrapperRef" : "ref";
      }
    }),
    _useResponsiveChildre2 = _slicedToArray(_useResponsiveChildre, 2),
    fittingChildren = _useResponsiveChildre2[0],
    overflowingChildren = _useResponsiveChildre2[1];
  var focusItems = fittingChildren.map(function () {
    return /*#__PURE__*/React.createRef();
  });
  if (overflowingChildren.length > 0 && overflowMenuRef.current) focusItems.push(overflowMenuRef);
  if (focusItems.length < focusedIndex) setFocusedIndex(0);
  var handleKeyNav = function handleKeyNav(e) {
    switch (e.key) {
      case KEYS.ARROW_RIGHT:
        {
          var _focusItems$nextIndex;
          e.preventDefault();
          var stepAmount = isDivider(fittingChildren[focusedIndex + 1]) ? 2 : 1;
          var nextIndex = Math.min(focusedIndex + stepAmount, focusItems.length - 1);
          (_focusItems$nextIndex = focusItems[nextIndex].current) === null || _focusItems$nextIndex === void 0 ? void 0 : _focusItems$nextIndex.focus();
          setFocusedIndex(nextIndex);
          break;
        }
      case KEYS.ARROW_LEFT:
        {
          var _focusItems$_nextInde;
          e.preventDefault();
          var _stepAmount = isDivider(fittingChildren[focusedIndex - 1]) ? 2 : 1;
          var _nextIndex = Math.max(focusedIndex - _stepAmount, 0);
          (_focusItems$_nextInde = focusItems[_nextIndex].current) === null || _focusItems$_nextInde === void 0 ? void 0 : _focusItems$_nextInde.focus();
          setFocusedIndex(_nextIndex);
          break;
        }
      case KEYS.END:
        {
          var _focusItems$current;
          e.preventDefault();
          (_focusItems$current = focusItems[focusItems.length - 1].current) === null || _focusItems$current === void 0 ? void 0 : _focusItems$current.focus();
          setFocusedIndex(focusItems.length - 1);
          break;
        }
      case KEYS.HOME:
        {
          var _focusItems$0$current;
          e.preventDefault();
          (_focusItems$0$current = focusItems[0].current) === null || _focusItems$0$current === void 0 ? void 0 : _focusItems$0$current.focus();
          setFocusedIndex(0);
          break;
        }
    }
  };
  var label = ariaLabel.toLowerCase().includes("toolbarButton") ? ariaLabel : "toolbarButton-".concat(ariaLabel);
  var buttonMenuChildren = overflowingChildren.filter(function (child) {
    return !isDivider(child);
  });
  return /*#__PURE__*/jsx(ActionToolbarGroupWrapper, _objectSpread(_objectSpread({
    ref: ref,
    "aria-label": label
  }, rest), {}, {
    children: /*#__PURE__*/jsxs("div", {
      style: {
        height: "100%"
      },
      ref: toolbarRef,
      children: [React.Children.map(fittingChildren, function (c, index) {
        return /*#__PURE__*/React.cloneElement(c, {
          onKeyDown: handleKeyNav,
          tabIndex: index === focusedIndex ? 0 : -1,
          ref: focusItems[index],
          onFocus: function onFocus() {
            return setFocusedIndex(index);
          },
          style: isDivider(c) && (index === 0 || index === fittingChildren.length - 1) ? _objectSpread(_objectSpread({}, c.props.style), {}, {
            display: "none"
          }) : c.props.style
        });
      }), overflowingChildren.length > 0 && /*#__PURE__*/jsx(ActionToolbarGroupMoreButton, {
        ref: ref,
        children: /*#__PURE__*/jsx(ButtonMenu, {
          buttonProps: {
            onKeyDown: handleKeyNav,
            onFocus: function onFocus() {
              return setFocusedIndex(focusItems.length - 1);
            },
            tabIndex: focusedIndex === focusItems.length - 1 ? 0 : -1,
            hideLabel: true,
            label: t("ActionToolbar.moreActions"),
            startAdornment: IconMore,
            variant: "ghost",
            ref: overflowMenuRef
          },
          options: buttonMenuChildren.map(function (child, i) {
            var _child$props = child.props,
              group = _child$props.group,
              childLabel = _child$props.label,
              onClick = _child$props.onClick;
            var nextChild = buttonMenuChildren[i + 1];
            var border = nextChild && nextChild.props.group !== group;
            return {
              children: childLabel,
              border: border,
              onClick: onClick
            };
          })
        })
      })]
    })
  }));
});
ActionToolbarGroup.displayName = "ActionToolbarGroup";

export { ActionToolbarGroup };
